import { deepEqual } from 'vuetify/lib/util/index.mjs'

export type WAutocompleteItemKey =
  | string
  | number
  | symbol
  | readonly (string | number)[]
  | ((item: unknown, fallback?: unknown) => unknown)

export type WAutocompleteValueComparator = (value: unknown, optionValue: unknown) => boolean

export interface WAutocompleteSelectAllOptions {
  itemTitle?: WAutocompleteItemKey | null
  itemValue?: WAutocompleteItemKey | null
  returnObject?: boolean
}

const DEFAULT_ITEM_TITLE = 'title'
const DEFAULT_ITEM_VALUE = 'value'

export function getAutocompleteSelectAllValues(
  items: unknown[],
  options: WAutocompleteSelectAllOptions = {}
): unknown[] {
  if (options.returnObject) {
    return items.slice()
  }

  return items.map((item) => resolveAutocompleteItemValue(item, options))
}

export function getAutocompleteSelectAllComparisonValues(
  items: unknown[],
  options: WAutocompleteSelectAllOptions = {}
): unknown[] {
  return items.map((item) => resolveAutocompleteItemValue(item, options))
}

export function areAutocompleteValuesAllSelected(
  selectedValues: unknown,
  optionValues: unknown[],
  options: WAutocompleteSelectAllOptions = {},
  comparator?: WAutocompleteValueComparator
) {
  if (!Array.isArray(selectedValues) || optionValues.length === 0) {
    return false
  }

  return optionValues.every((optionValue) =>
    selectedValues.some((selectedValue) => isAutocompleteValueSelected(selectedValue, optionValue, options, comparator))
  )
}

export function resolveAutocompleteItemValue(item: unknown, options: WAutocompleteSelectAllOptions = {}) {
  const title = resolveAutocompleteItemKey(item, options.itemTitle ?? DEFAULT_ITEM_TITLE, item)
  return resolveAutocompleteItemKey(item, options.itemValue ?? DEFAULT_ITEM_VALUE, title)
}

function resolveAutocompleteItemKey(item: unknown, key: WAutocompleteItemKey | null | undefined, fallback?: unknown) {
  if (key === null || key === undefined) {
    return fallback
  }

  if (typeof key === 'function') {
    return key(item, fallback)
  }

  if (isAutocompleteItemPath(key)) {
    return getNestedValue(item, key, fallback)
  }

  if (!isRecord(item)) {
    return fallback
  }

  if (key in item) {
    return item[key]
  }

  if (typeof key === 'string' && key.includes('.')) {
    return getNestedValue(item, key.split('.'), fallback)
  }

  return fallback
}

function getNestedValue(item: unknown, path: readonly (string | number)[], fallback?: unknown) {
  let currentValue = item

  for (const key of path) {
    if (!isRecord(currentValue) || !(key in currentValue)) {
      return fallback
    }

    currentValue = currentValue[key]
  }

  return currentValue
}

function isRecord(value: unknown): value is Record<string | number | symbol, unknown> {
  return typeof value === 'object' && value !== null
}

function isAutocompleteItemPath(
  key: WAutocompleteItemKey | null | undefined
): key is readonly (string | number)[] {
  return Array.isArray(key)
}

export function isEqualAutocompleteValue(
  selectedValue: unknown,
  optionValue: unknown,
  comparator?: WAutocompleteValueComparator
) {
  if (comparator) {
    return comparator(selectedValue, optionValue)
  }

  return deepEqual(selectedValue, optionValue)
}

export function isAutocompleteValueSelected(
  selectedValue: unknown,
  optionValue: unknown,
  options: WAutocompleteSelectAllOptions = {},
  comparator?: WAutocompleteValueComparator
) {
  if (selectedValue === undefined) {
    return false
  }

  if (selectedValue === null) {
    return optionValue === null
  }

  const comparisonValue = options.returnObject ? resolveAutocompleteItemValue(selectedValue, options) : selectedValue

  if (comparisonValue === null || optionValue === null) {
    return comparisonValue === optionValue
  }

  if (comparator) {
    return comparator(selectedValue, optionValue)
  }

  return isEqualAutocompleteValue(comparisonValue, optionValue)
}
